import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { exerciseImage, exerciseVideo } from '../lib/hooks';
import { useT } from '../i18n';

export function Icon({ name, size, className = '', outline, style, title }) {
  return (
    <span
      className={`gm-icon${outline ? ' outline' : ''} ${className}`}
      style={size ? { fontSize: size, ...style } : style}
      aria-hidden={title ? undefined : true}
      title={title}
    >
      {name}
    </span>
  );
}

export function Spinner({ large, style }) {
  return <span className={`gm-spinner${large ? ' gm-spinner--lg' : ''}`} style={style} role="status" />;
}

export function Loading() {
  return (
    <div className="gm-center">
      <Spinner large />
    </div>
  );
}

export function Switch({ checked, onChange, disabled, label }) {
  return (
    <label className="gm-switch" aria-label={label}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span />
    </label>
  );
}

export function Empty({ icon = 'inbox', children }) {
  return (
    <div className="gm-empty">
      <Icon name={icon} />
      <div>{children}</div>
    </div>
  );
}

export function SectionTitle({ children, right }) {
  const t = useT();
  return (
    <div className="gm-row-between" style={{ marginBottom: 10 }}>
      <h2 className="gm-section-title" style={{ margin: 0 }}>{typeof children === 'string' ? t.upper(children) : children}</h2>
      {right}
    </div>
  );
}

/* ───────── Modal ───────── */
export function Modal({ open, onClose, title, children, footer, wide, closeOnScrim = true }) {
  const t = useT();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="gm gm-modal-root" style={{ minHeight: 0, background: 'transparent' }} role="dialog" aria-modal="true">
      <div className="gm-modal-scrim" onClick={closeOnScrim ? onClose : undefined} />
      <div className={`gm-modal${wide ? ' gm-modal--wide' : ''}`}>
        <div className="gm-modal__head">
          <div className="gm-modal__title">{title}</div>
          {onClose && (
            <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={onClose} aria-label={t('close')}>
              <Icon name="close" />
            </button>
          )}
        </div>
        <div className="gm-modal__body">{children}</div>
        {footer && <div className="gm-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ───────── Confirm dialog (imperative) ───────── */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const t = useT();
  const [state, setState] = useState(null);
  const confirm = useCallback((opts) => new Promise((resolve) => setState({ ...opts, resolve })), []);
  const close = (v) => {
    state?.resolve(v);
    setState(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={!!state}
        onClose={() => close(false)}
        title={state?.title}
        footer={(
          <>
            <button type="button" className="gm-btn gm-btn--ghost" onClick={() => close(false)}>{state?.cancelLabel || t('cancel')}</button>
            <button type="button" className={`gm-btn${state?.danger ? ' gm-btn--danger' : ''}`} onClick={() => close(true)}>
              {state?.confirmLabel || t('ok')}
            </button>
          </>
        )}
      >
        <p style={{ whiteSpace: 'pre-line' }} className="gm-muted">{state?.message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);

/* ───────── Toasts ───────── */
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  const dismiss = useCallback((id) => {
    setToasts((l) => l.filter((x) => x.id !== id));
    clearTimeout(timers.current[id]);
  }, []);
  const show = useCallback((message, { type = 'info', duration = 3500, action } = {}) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((l) => [...l.slice(-2), { id, message, type, action }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);
  const api = useMemo(() => ({
    show,
    success: (m, o) => show(m, { ...o, type: 'success' }),
    error: (m, o) => show(m, { ...o, type: 'error' }),
    warning: (m, o) => show(m, { ...o, type: 'warning' }),
  }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="gm gm-toasts" style={{ minHeight: 0, background: 'transparent' }}>
          {toasts.map((x) => (
            <div key={x.id} className={`gm-toast gm-toast--${x.type}`}>
              <span className="gm-grow">{x.message}</span>
              {x.action && (
                <button type="button" onClick={() => { x.action.onClick(); dismiss(x.id); }}>{x.action.label}</button>
              )}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ───────── Menu (popover) ───────── */
export function Menu({ trigger, children, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);
  return (
    <div className="gm-menu-wrap" ref={ref}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div className="gm-menu" style={align === 'left' ? { left: 0, right: 'auto' } : undefined} onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ───────── Exercise thumbnail ───────── */
/**
 * Plays the mp4 like a GIF: muted, looping, no controls, not interactive.
 * Shows the photo as poster until it starts; falls back to the photo, then to `fallback`.
 */
function ExerciseMedia({ exercise, alt = '', style, fallback = null }) {
  // Remember which URLs failed rather than a flag, so a reused element showing a
  // different exercise retries its own video/photo.
  const [brokenVideo, setBrokenVideo] = useState(null);
  const [brokenImage, setBrokenImage] = useState(null);
  const ref = useRef(null);
  const video = exerciseVideo(exercise);
  const image = exerciseImage(exercise);
  const videoBroken = video === brokenVideo;
  const imageBroken = image === brokenImage;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // React only sets `muted` as a property, and some browsers (iOS Safari) refuse
    // to autoplay without it being set before play(); force it and kick playback.
    el.muted = true;
    el.defaultMuted = true;
    // Browsers pause background tabs / interrupted media; resume like a GIF would.
    const play = () => {
      if (el.paused && document.visibilityState === 'visible') el.play().catch(() => {});
    };
    play();
    document.addEventListener('visibilitychange', play);
    el.addEventListener('pause', play);
    return () => {
      document.removeEventListener('visibilitychange', play);
      el.removeEventListener('pause', play);
    };
  }, [video, videoBroken]);

  if (video && !videoBroken) {
    return (
      <video
        ref={ref}
        className="gm-gif-video"
        src={video}
        poster={(!imageBroken && image) || undefined}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        controls={false}
        preload="auto"
        tabIndex={-1}
        aria-label={alt || undefined}
        style={style}
        onError={() => setBrokenVideo(video)}
        onContextMenu={(e) => e.preventDefault()}
      />
    );
  }
  if (image && !imageBroken) return <img src={image} alt={alt} loading="lazy" style={style} onError={() => setBrokenImage(image)} />;
  return fallback;
}

export function ExerciseThumb({ exercise, large, onClick }) {
  return (
    <div className={`gm-ex-thumb${large ? ' gm-ex-thumb--lg' : ''}`} onClick={onClick} style={onClick ? { cursor: 'zoom-in' } : undefined}>
      <ExerciseMedia exercise={exercise} fallback={<Icon name="fitness_center" />} />
    </div>
  );
}

export function ImagePreview({ exercise, onClose }) {
  const t = useT();
  if (!exercise) return null;
  return (
    <Modal open onClose={onClose} title={exercise.name}>
      <ExerciseMedia
        exercise={exercise}
        alt={exercise.name}
        style={{ width: '100%', borderRadius: 12, display: 'block' }}
        fallback={(
          <div className="gm-muted" style={{ textAlign: 'center', padding: '24px 0' }}>
            <Icon name="fitness_center" size={56} />
            <p>{t('previewNotAvailable')}</p>
          </div>
        )}
      />
      <div className="gm-row gm-wrap" style={{ marginTop: 12 }}>
        {exercise.muscle && <span className="gm-pill">{t.muscle(exercise.muscle)}</span>}
        {exercise.equipment && <span className="gm-pill">{t.equipment(exercise.equipment)}</span>}
        {exercise.difficulty && <span className="gm-pill">{t(exercise.difficulty)}</span>}
      </div>
    </Modal>
  );
}

/* ───────── Ring ───────── */
export function Ring({ value, goal, color, label, unit = 'g' }) {
  const pct = goal > 0 ? Math.min(1, value / goal) : 0;
  const r = 33;
  const c = 2 * Math.PI * r;
  return (
    <div className="gm-ring">
      <div className="gm-ring__wrap">
        <svg viewBox="0 0 78 78">
          <circle cx="39" cy="39" r={r} fill="none" stroke={color} strokeOpacity="0.18" strokeWidth="7" />
          <circle
            cx="39"
            cy="39"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct)}
            style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.22,1,0.36,1)' }}
          />
        </svg>
        <div className="gm-ring__pct">{Math.round((goal > 0 ? value / goal : 0) * 100)}%</div>
      </div>
      <div className="gm-ring__label" style={{ color }}>{label}</div>
      <div className="gm-ring__val">{value} / {goal}{unit}</div>
    </div>
  );
}

/* ───────── Celebration overlay ───────── */
// Port of the app's CelebrationOverlay + CelebrationQueue: the page dims and lights
// up from a badge that pops in, shockwave rings and a confetti burst fan out from
// it and the message slides up beneath. Shows queue up and play one after another,
// so a meal and the badge it unlocked don't land on top of each other.
const CELEBRATION_MS = 3000;
const CELEBRATION_GAP_MS = 300;
const SPARKLE = '#FFD54F';

function hexRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const mixRgb = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const rgba = ([r, g, b], a = 1) => `rgba(${r}, ${g}, ${b}, ${a})`;
const prefersReducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function makeParticles(rgb) {
  const palette = [rgba(rgb), rgba(mixRgb(rgb, [255, 255, 255], 0.45)), SPARKLE, '#ffffff'];
  return Array.from({ length: 44 }, (_, i) => {
    // Mostly upward, fanning wide to both sides.
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.5;
    const speed = 380 + Math.random() * 520;
    return {
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 5 + Math.random() * 6,
      spin: (Math.random() - 0.5) * 14,
      round: Math.random() < 0.35,
      color: palette[i % palette.length],
      delay: Math.random() * 0.06,
    };
  });
}

/** Canvas confetti burst + shockwave rings, the app's _BurstPainter. */
function useBurst(canvasRef, rgb, centerY) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion()) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };
    resize();
    window.addEventListener('resize', resize);
    const particles = makeParticles(rgb);
    const ring = mixRgb(rgb, [255, 255, 255], 0.3);
    const total = CELEBRATION_MS / 1000;
    const start = performance.now();
    let frame;
    const draw = (now) => {
      const t = Math.min(1, (now - start) / CELEBRATION_MS);
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      const cx = w / 2;
      const cy = h * centerY;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      [[0.04, 190], [0.12, 260]].forEach(([begin, maxR]) => {
        const s = (t - begin) / 0.4;
        if (s <= 0 || s >= 1) return;
        const eased = 1 - (1 - s) ** 3;
        ctx.beginPath();
        ctx.arc(cx, cy, 48 + (maxR - 48) * eased, 0, Math.PI * 2);
        ctx.lineWidth = 3 * (1 - s) + 0.5;
        ctx.strokeStyle = rgba(ring, 0.7 * (1 - s));
        ctx.stroke();
      });
      particles.forEach((p) => {
        const e = (t - 0.06 - p.delay) * total;
        if (e <= 0) return;
        // Air drag slows the outward burst; gravity pulls it back down.
        const travel = (1 - Math.exp(-2.4 * e)) / 2.4;
        const alpha = Math.max(0, 1 - e / (total * 0.75));
        if (alpha <= 0) return;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.translate(cx + p.vx * travel, cy + p.vy * travel + 0.5 * 520 * e * e);
        ctx.rotate(p.spin * e);
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.size * 0.45, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Flutter the confetti by squashing it along its spin.
          const ph = p.size * 0.5 * (Math.abs(Math.cos(p.spin * e * 1.7)) * 0.8 + 0.2);
          ctx.fillRect(-p.size / 2, -ph / 2, p.size, ph);
        }
        ctx.restore();
      });
      if (t < 1) frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, [canvasRef, rgb, centerY]);
}

function CelebrationShow({ emoji, medal, message, color = '#FF6D00', onDone }) {
  const canvasRef = useRef(null);
  const rgb = useMemo(() => hexRgb(color), [color]);
  useBurst(canvasRef, rgb, 0.34);
  useEffect(() => {
    const id = setTimeout(onDone, CELEBRATION_MS);
    return () => clearTimeout(id);
  }, [onDone]);
  return createPortal(
    <div
      className={`gm gm-celebrate${medal ? ' gm-celebrate--medal' : ''}`}
      style={{ minHeight: 0, background: 'transparent', '--gm-cel-glow': rgba(rgb, 0.55), '--gm-cel-glow-soft': rgba(rgb, 0.18) }}
      role="status"
      aria-live="polite"
    >
      <div className="gm-celebrate__backdrop" />
      <canvas ref={canvasRef} className="gm-celebrate__burst" />
      <div className="gm-celebrate__hero">
        <div className="gm-celebrate__pop">
          {medal || (
            <div
              className="gm-celebrate__disc"
              style={{
                background: `linear-gradient(135deg, ${rgba(mixRgb(rgb, [255, 255, 255], 0.35))}, ${color}, ${rgba(mixRgb(rgb, [0, 0, 0], 0.2))})`,
                boxShadow: `0 0 32px 4px ${rgba(rgb, 0.6)}`,
              }}
            >
              {emoji}
            </div>
          )}
        </div>
      </div>
      <div className="gm-celebrate__msg">
        <span>{message}</span>
      </div>
    </div>,
    document.body,
  );
}

/* Compact celebration: the app's CompactCelebrationOverlay, e.g. the XP a meal
   just earned. A glowing pill pops in on the upper third with a sparkle burst,
   counts up to `count`, shines, then shrinks and flies into the level frame
   (the visible [data-xp-target]: the avatar on phones, the sidebar card on
   desktop), bursting there. */
const COMPACT_MS = 2300;
const C_POP_END = 0.2;
const C_COUNT = [0.1, 0.42];
const C_SHINE = [0.4, 0.6];
const C_FLY = [0.62, 0.9];

const span = (t, a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));
const easeOutCubic = (x) => 1 - (1 - x) ** 3;
const easeInCubic = (x) => x ** 3;
const easeOutBack = (x) => 1 + 2.70158 * (x - 1) ** 3 + 1.70158 * (x - 1) ** 2;
const elasticOut = (x) => (x === 0 || x === 1 ? x : 2 ** (-10 * x) * Math.sin((x * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1);
const quad = (a, c, b, t) => ({
  x: (1 - t) ** 2 * a.x + 2 * (1 - t) * t * c.x + t * t * b.x,
  y: (1 - t) ** 2 * a.y + 2 * (1 - t) * t * c.y + t * t * b.y,
});

function makeSparks(count) {
  return Array.from({ length: count }, (_, i) => ({
    angle: (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.5,
    distance: 70 + Math.random() * 90,
    size: 3 + Math.random() * 5,
    star: Math.random() < 0.5,
    tint: i % 3,
  }));
}

function drawSpark(ctx, x, y, size, star, color, alpha) {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  if (star) {
    // Four-point twinkle.
    ctx.moveTo(x, y - size);
    ctx.quadraticCurveTo(x, y, x + size, y);
    ctx.quadraticCurveTo(x, y, x, y + size);
    ctx.quadraticCurveTo(x, y, x - size, y);
    ctx.quadraticCurveTo(x, y, x, y - size);
  } else {
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
  }
  ctx.fill();
}

function xpTargetPoint() {
  const el = [...document.querySelectorAll('[data-xp-target]')].find((e) => e.getBoundingClientRect().width > 0);
  if (!el) return { x: window.innerWidth - 40, y: 32 };
  const r = el.getBoundingClientRect();
  // The sidebar card is wide: aim at its level badge on the left.
  return r.width > 120 ? { x: r.left + 30, y: r.top + r.height / 2 } : { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

const buzz = (ms) => {
  try {
    navigator.vibrate?.(ms);
  } catch (e) {
    // not supported
  }
};

function CompactCelebrationShow({ emoji, title, count, countTitle, message, color = '#FDD835', onDone }) {
  const [t, setT] = useState(0);
  const canvasRef = useRef(null);
  const reduced = useMemo(() => prefersReducedMotion(), []);
  const rgb = useMemo(() => hexRgb(color), [color]);
  const geo = useMemo(() => {
    const start = { x: window.innerWidth / 2, y: window.innerHeight * 0.3 };
    const target = xpTargetPoint();
    // Arcs up and to the side instead of a straight line.
    const control = { x: start.x + (target.x - start.x) * 0.2, y: start.y - (start.y - target.y) * 0.9 };
    return { start, target, control, burst: makeSparks(22), landing: makeSparks(12) };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    if (canvas) {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    }
    const tints = [rgba(rgb), '#ffffff', SPARKLE];
    const begin = performance.now();
    let landed = false;
    let frame;
    buzz(12);
    const burst = (cx, cy, sparks, p, reach) => {
      if (p <= 0 || p >= 1) return;
      const out = easeOutCubic(p);
      sparks.forEach((s) => {
        const d = s.distance * reach * out;
        drawSpark(ctx, cx + Math.cos(s.angle) * d, cy + Math.sin(s.angle) * d, s.size * (1 - 0.4 * p), s.star, tints[s.tint], 1 - p * p);
      });
    };
    const tick = () => {
      // Same clock as `begin` (the frame timestamp's origin can differ outside browsers).
      const time = Math.min(1, (performance.now() - begin) / COMPACT_MS);
      setT(time);
      if (!landed && time >= C_FLY[1]) {
        landed = true;
        buzz(25);
      }
      if (ctx && !reduced) {
        const { start, target, control } = geo;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const ringP = span(time, 0.02, 0.5);
        if (ringP > 0 && ringP < 1) {
          const ring = easeOutCubic(ringP);
          ctx.globalAlpha = 1;
          ctx.beginPath();
          ctx.arc(start.x, start.y, 30 + 110 * ring, 0, Math.PI * 2);
          ctx.lineWidth = 3 * (1 - ring) + 0.3;
          ctx.strokeStyle = rgba(rgb, 0.5 * (1 - ring));
          ctx.stroke();
        }
        burst(start.x, start.y, geo.burst, ringP, 1.4);
        if (time >= C_FLY[0] && time < C_FLY[1]) {
          const fly = easeInCubic(span(time, C_FLY[0], C_FLY[1]));
          for (let i = 1; i <= 8; i += 1) {
            const back = fly - i * 0.045;
            if (back <= 0) break;
            const p = quad(start, control, target, back);
            drawSpark(ctx, p.x, p.y, 6 - i * 0.5, i % 2 === 1, tints[i % 3], 0.8 * (1 - i / 9));
          }
        }
        burst(target.x, target.y, geo.landing, span(time, C_FLY[1], 1), 0.45);
        ctx.globalAlpha = 1;
      }
      if (time < 1) frame = requestAnimationFrame(tick);
      else onDone();
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [geo, rgb, reduced, onDone]);

  const raw = (count || 0) * easeOutCubic(span(t, C_COUNT[0], C_COUNT[1]));
  const shown = Math.round(raw);
  const label = count != null && countTitle ? countTitle(shown) : title;
  let style;
  let bump = 0;
  if (reduced) {
    style = { left: geo.start.x, top: geo.start.y, opacity: span(t, 0, 0.1) * (1 - span(t, 0.8, 1)) };
  } else {
    const pop = elasticOut(span(t, 0, C_POP_END));
    const fly = easeInCubic(span(t, C_FLY[0], C_FLY[1]));
    const pos = quad(geo.start, geo.control, geo.target, fly);
    const scale = (0.3 + 0.7 * pop) * (1 - 0.78 * fly);
    style = {
      left: pos.x,
      top: pos.y,
      opacity: span(t, 0, 0.06) * (1 - span(t, C_FLY[1] - 0.04, C_FLY[1] + 0.02)),
      transform: `translate(-50%, -50%) scale(${scale})`,
    };
    if (count && t >= C_COUNT[0] && t <= C_COUNT[1] + 0.04) {
      const r = raw + 0.5;
      bump = 0.16 * (1 - (r - Math.floor(r)));
    }
  }
  const glow = 0.6 + 0.4 * Math.sin(span(t, C_POP_END, C_FLY[0]) * Math.PI * 2);
  const shine = span(t, C_SHINE[0], C_SHINE[1]);
  const spin = easeOutBack(span(t, 0, 0.35));

  return createPortal(
    <div className="gm gm-xp-burst" style={{ minHeight: 0, background: 'transparent' }} role="status" aria-live="polite">
      <canvas ref={canvasRef} className="gm-xp-burst__canvas" />
      <div
        className="gm-xp-pill"
        style={{
          ...style,
          background: `linear-gradient(135deg, ${rgba(mixRgb(rgb, [255, 255, 255], 0.35))}, ${color})`,
          boxShadow: `0 0 ${28 * glow}px ${4 * glow}px ${rgba(rgb, 0.55 * glow)}`,
          '--gm-shine': shine > 0 && shine < 1 ? `${-60 + 220 * shine}%` : '-200%',
        }}
      >
        <span className="gm-xp-pill__icon" style={{ transform: reduced ? undefined : `rotate(${(1 - spin) * -360}deg)` }}>{emoji}</span>
        <span className="gm-xp-pill__text">
          <span className="gm-xp-pill__title" style={{ transform: `scale(${1 + bump})` }}>{label}</span>
          <span className="gm-xp-pill__msg">{message}</span>
        </span>
      </div>
    </div>,
    document.body,
  );
}

const CelebrationContext = createContext(() => {});

/**
 * Plays celebrations one at a time: `useCelebrate()({ emoji | medal, message, color, onDone })`.
 * With `compact: true` (plus `title`, optional `count` + `countTitle`) it plays the
 * small XP-style pill instead of the full-screen overlay.
 */
export function CelebrationProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [resting, setResting] = useState(false);
  const celebrate = useCallback((c) => {
    setQueue((q) => [...q, { ...c, key: Math.random().toString(36).slice(2) }]);
  }, []);
  const current = queue[0];
  const done = useCallback(() => {
    current?.onDone?.();
    // A breather so each one reads as its own moment.
    setResting(true);
    setTimeout(() => {
      setQueue((q) => q.slice(1));
      setResting(false);
    }, CELEBRATION_GAP_MS);
  }, [current]);
  return (
    <CelebrationContext.Provider value={celebrate}>
      {children}
      {current && !resting && (current.compact ? (
        <CompactCelebrationShow
          key={current.key}
          emoji={current.emoji}
          title={current.title}
          count={current.count}
          countTitle={current.countTitle}
          message={current.message}
          color={current.color}
          onDone={done}
        />
      ) : (
        <CelebrationShow key={current.key} emoji={current.emoji} medal={current.medal} message={current.message} color={current.color} onDone={done} />
      ))}
    </CelebrationContext.Provider>
  );
}

export const useCelebrate = () => useContext(CelebrationContext);

export function initials(user) {
  const s = user?.firstName || user?.email || '?';
  return s.charAt(0).toLocaleUpperCase('tr-TR');
}

export const STORE_LINKS = {
  play: 'https://play.google.com/store/apps/details?id=com.jovisio.gymgm',
  ios: 'https://apps.apple.com/us/app/gymgm-workout-nutrition/id6762042149',
};

/** Single "get the app" link: App Store on Apple devices, Google Play elsewhere. */
export function appStoreLink() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return /iPhone|iPad|iPod|Macintosh/i.test(ua) ? STORE_LINKS.ios : STORE_LINKS.play;
}
